import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ScanOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { getData, getErrorMessage, isSuccess, rfidAdminApi, toDisplayText } from "../../hooks/rfidAdminApi";
import "./RfidAdmin.css";

const { Text, Title } = Typography;

const TagAdmin = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [tags, setTags] = useState([]);
  const [unregistered, setUnregistered] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [pairingSince, setPairingSince] = useState(null);
  const [latestScan, setLatestScan] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [serverError, setServerError] = useState("");
  const assigningRef = useRef(false);
  const refreshingRef = useRef(false);

  const selectedClass = useMemo(
    () => classes.find((item) => String(item.id) === String(selectedClassId)),
    [classes, selectedClassId]
  );
  const selectedStudent = useMemo(
    () => students.find((item) => String(item.id) === String(selectedStudentId)),
    [students, selectedStudentId]
  );

  const describeRejected = (items) => {
    const rejected = items.find((item) => item.status === "rejected");
    return rejected ? getErrorMessage(rejected.reason, "서버 데이터를 불러오지 못했습니다.") : "";
  };

  const loadBase = async ({ silent = false } = {}) => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    if (!silent) setLoading(true);

    try {
      const results = await Promise.allSettled([
        rfidAdminApi.getClasses(),
        rfidAdminApi.getTags(),
        rfidAdminApi.getUnregistered(30),
      ]);

      const [classResult, tagResult, recentResult] = results;
      if (classResult.status === "fulfilled") {
        const classRows = getData(classResult.value);
        const safeClassRows = Array.isArray(classRows) ? classRows : [];
        setClasses(safeClassRows);
        setSelectedClassId((current) => current || safeClassRows[0]?.id || null);
      }
      if (tagResult.status === "fulfilled") {
        const rows = getData(tagResult.value);
        setTags(Array.isArray(rows) ? rows : []);
      }
      if (recentResult.status === "fulfilled") {
        const rows = getData(recentResult.value);
        setUnregistered(Array.isArray(rows) ? rows : []);
      }

      const errorMessage = describeRejected(results);
      if (errorMessage) {
        setServerError(errorMessage);
      } else {
        setServerError("");
      }
    } catch (error) {
      setServerError(getErrorMessage(error, "카드 등록 정보를 불러오지 못했습니다."));
    } finally {
      if (!silent) setLoading(false);
      refreshingRef.current = false;
    }
  };

  const loadStudents = async (classId) => {
    try {
      const data = getData(await rfidAdminApi.getStudents(classId));
      const rows = Array.isArray(data) ? data : [];
      setStudents(rows);
      setSelectedStudentId((current) => rows.some((item) => String(item.id) === String(current)) ? current : null);
      setServerError("");
    } catch (error) {
      setServerError(getErrorMessage(error, "학생 목록을 불러오지 못했습니다."));
    }
  };

  useEffect(() => {
    loadBase();
    const timer = setInterval(() => loadBase({ silent: true }), 10000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedClassId) loadStudents(selectedClassId);
    else setStudents([]);
    setSelectedStudentId(null);
    setWaiting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId]);

  const assignScannedCard = async (scan, studentId = selectedStudentId, classId = selectedClassId) => {
    if (!scan?.epc || !studentId || !classId || assigningRef.current) return;
    assigningRef.current = true;
    setWaiting(false);
    setLatestScan(scan);
    try {
      const res = await rfidAdminApi.scanAssign({
        epc: scan.epc,
        tid: scan.tid || undefined,
        classId,
        studentId,
      });
      if (!isSuccess(res)) throw new Error(res?.error || "카드 등록 실패");
      setLastResult(res.data);
      setServerError("");
      const student = res.data?.student;
      messageApi.success(`${student?.name || "학생"} 카드로 등록했습니다.`);
      await Promise.allSettled([loadBase({ silent: true }), loadStudents(classId)]);
    } catch (error) {
      const text = getErrorMessage(error, "카드 등록 중 오류가 발생했습니다.");
      setServerError(text);
      messageApi.error(text);
    } finally {
      assigningRef.current = false;
      setPairingSince(null);
    }
  };

  useEffect(() => {
    if (!waiting || !pairingSince) return undefined;
    let cancelled = false;
    const poll = async () => {
      if (assigningRef.current) return;
      try {
        const res = await rfidAdminApi.getLatestScan(pairingSince);
        const scan = res?.data || null;
        if (!cancelled && scan?.epc) await assignScannedCard(scan);
      } catch (error) {
        if (!cancelled) setServerError(getErrorMessage(error, "최근 태그를 확인하지 못했습니다."));
      }
    };
    poll();
    const timer = setInterval(poll, 1000);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, pairingSince, selectedClassId, selectedStudentId]);

  const startPairing = () => {
    if (!selectedClassId || !selectedStudentId) {
      messageApi.warning("반과 학생을 먼저 선택해주세요.");
      return;
    }
    setLatestScan(null);
    setLastResult(null);
    setServerError("");
    setPairingSince(new Date(Date.now() - 300).toISOString());
    setWaiting(true);
  };

  const quickAssign = (row) => {
    if (!selectedClassId || !selectedStudentId) {
      messageApi.warning("위에서 반과 학생을 먼저 선택해주세요.");
      return;
    }
    assignScannedCard(row);
  };

  const toggle = async (row, checked) => {
    try { await rfidAdminApi.updateTag(row.id, { isActive: checked }); await loadBase({ silent: true }); }
    catch (error) { setServerError(getErrorMessage(error)); }
  };
  const remove = async (id) => {
    try { await rfidAdminApi.deleteTag(id); await loadBase({ silent: true }); }
    catch (error) { setServerError(getErrorMessage(error)); }
  };

  const recentColumns = [
    { title: "마지막 판독", dataIndex: "last_read_at", width: 180, render: (v) => v ? new Date(v).toLocaleString() : "-" },
    { title: "EPC", dataIndex: "epc", render: (v) => <code>{v}</code> },
    { title: "TID", dataIndex: "tid", render: (v) => v ? <code>{v}</code> : "-" },
    { title: "리더", dataIndex: "reader_name", render: (v, r) => `${v || "-"}${r.reader_location ? ` · ${r.reader_location}` : ""}` },
    { title: "등록", width: 120, render: (_, row) => <Button type="primary" size="small" onClick={() => quickAssign(row)}>선택 학생에게</Button> },
  ];

  const tagColumns = [
    { title: "카드명", dataIndex: "tag_label" },
    { title: "학생", render: (_, row) => row.student_name ? <Tag color="green">{row.class_name} {row.student_no ? `${row.student_no}번 ` : ""}{row.student_name}</Tag> : <Tag>미배정</Tag> },
    { title: "EPC", dataIndex: "epc", render: (v) => <code>{v}</code> },
    { title: "활성", dataIndex: "is_active", width: 80, render: (v, row) => <Switch checked={Boolean(v)} onChange={(checked) => toggle(row, checked)} /> },
    { title: "삭제", width: 70, render: (_, row) => <Popconfirm title="카드 삭제" description="학생 배정도 해제됩니다." onConfirm={() => remove(row.id)}><Button danger size="small" icon={<DeleteOutlined />} /></Popconfirm> },
  ];

  return (
    <div className="rfid-page">
      {contextHolder}
      <div className="rfid-page-header">
        <div>
          <h1 className="rfid-page-title">카드 바로 등록</h1>
          <p className="rfid-page-desc">학생을 고른 뒤 등록 시작을 누르고 카드 한 장을 태그하면 EPC 등록과 학생 배정이 한 번에 끝납니다.</p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => loadBase()}>새로고침</Button>
      </div>

      {serverError && (
        <Alert
          className="rfid-section-gap"
          type="error"
          showIcon
          message="서버 또는 MySQL 연결을 확인해주세요."
          description={toDisplayText(serverError, "서버 요청을 처리하지 못했습니다.")}
          action={<Button size="small" onClick={() => loadBase()}>다시 시도</Button>}
        />
      )}

      <div className="rfid-grid-2 rfid-section-gap">
        <Card title="1. 학생 선택">
          <Space direction="vertical" size={14} style={{ width: "100%" }}>
            <Select
              style={{ width: "100%" }}
              placeholder="반 선택"
              value={selectedClassId || undefined}
              onChange={setSelectedClassId}
              options={classes.map((item) => ({ value: item.id, label: `${item.school_year}학년 ${item.name}` }))}
            />
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              placeholder="학생 선택"
              value={selectedStudentId || undefined}
              onChange={setSelectedStudentId}
              options={students.filter((item) => item.is_active).map((item) => ({
                value: item.id,
                label: `${item.student_no ? `${item.student_no}번 ` : ""}${item.name}${item.tag_label ? ` · 현재 ${item.tag_label}` : ""}`,
              }))}
            />
            <Button
              type="primary"
              size="large"
              block
              icon={waiting ? <StopOutlined /> : <ScanOutlined />}
              danger={waiting}
              onClick={waiting ? () => { setWaiting(false); setPairingSince(null); } : startPairing}
              disabled={!selectedClassId || !selectedStudentId}
            >
              {waiting ? "등록 대기 취소" : "2. 카드 등록 시작"}
            </Button>
          </Space>
        </Card>

        <Card title="3. 카드를 안테나에 한 장만 대세요" className={waiting ? "rfid-pairing-card-active" : ""}>
          {waiting ? (
            <div className="rfid-pairing-wait">
              <Spin size="large" />
              <Title level={3} style={{ margin: 0 }}>카드 입력 대기 중</Title>
              <Text>{selectedClass?.school_year}학년 {selectedClass?.name} · {selectedStudent?.student_no ? `${selectedStudent.student_no}번 ` : ""}{selectedStudent?.name}</Text>
              <Text type="secondary">다른 카드는 안테나에서 치워주세요. 다음으로 감지되는 카드가 자동 등록됩니다.</Text>
            </div>
          ) : lastResult ? (
            <div className="rfid-pairing-wait">
              <CheckCircleOutlined className="rfid-pairing-success-icon" />
              <Title level={3} style={{ margin: 0 }}>등록 완료</Title>
              <Text strong>{lastResult.student?.name} → {lastResult.tag?.tag_label}</Text>
              <code>{lastResult.tag?.epc}</code>
              <Button type="primary" onClick={startPairing}>같은 학생 카드 다시 등록</Button>
            </div>
          ) : (
            <Alert
              type="info"
              showIcon
              message="반과 학생을 선택한 뒤 ‘카드 등록 시작’을 누르세요."
              description="별도의 태그명 입력이나 태그 배정 메뉴 없이, 다음에 읽힌 EPC를 선택한 학생 카드로 자동 등록합니다."
            />
          )}
          {latestScan?.epc && <div className="rfid-last-scan"><span>최근 감지 EPC</span><code>{latestScan.epc}</code></div>}
        </Card>
      </div>

      <Card className="rfid-section-gap" title="최근 미등록 카드" extra="10초마다 갱신">
        <Table rowKey="epc" size="small" loading={loading} dataSource={unregistered} columns={recentColumns} pagination={{ pageSize: 6 }} scroll={{ x: 900 }} />
      </Card>

      <Card className="rfid-section-gap" title="등록된 카드">
        <Table rowKey="id" size="small" loading={loading} dataSource={tags} columns={tagColumns} pagination={{ pageSize: 12 }} scroll={{ x: 900 }} />
      </Card>
    </div>
  );
};

export default TagAdmin;
